import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { dashboardKeys } from "@/lib/api/dashboard";
import type {
  ApplicationDetailsResponse,
  ApplicationDeploymentResponse,
  DeploymentLogsResponse,
  UpdateApplicationResponse,
  SyncApplicationResponse,
} from "@/lib/api/types";

export const applicationsKeys = {
  all: ["applications"] as const,
  detail: (applicationId: string) =>
    [...applicationsKeys.all, "detail", applicationId] as const,
  deployment: (applicationId: string) =>
    [...applicationsKeys.all, "deployment", applicationId] as const,
  deploymentLogs: (applicationId: string) =>
    [...applicationsKeys.all, "deployment-logs", applicationId] as const,
};

export const applicationQueryOptions = (applicationId: string) =>
  queryOptions({
    queryKey: applicationsKeys.detail(applicationId),
    queryFn: () =>
      apiClient
        .get<ApplicationDetailsResponse>(`/api/applications/${applicationId}`)
        .then((r) => r.data),
    enabled: !!applicationId,
  });

export function useApplication(applicationId: string) {
  return useQuery(applicationQueryOptions(applicationId));
}

export function useUpdateApplication(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      apiClient
        .patch<UpdateApplicationResponse>(
          `/api/applications/${applicationId}`,
          {
            name,
          }
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.detail(applicationId),
      });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useSyncApplication(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient
        .post<SyncApplicationResponse>(
          `/api/applications/${applicationId}/sync`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.detail(applicationId),
      });
    },
  });
}

export interface CustomizeBotPayload {
  username?: string;
  avatar?: string | null;
  description?: string;
  icon?: string | null;
  presenceStatus?: "online" | "idle" | "dnd";
  activityType?:
    | "Playing"
    | "Streaming"
    | "Listening"
    | "Watching"
    | "Competing"
    | "Custom"
    | null;
  activityName?: string | null;
  activityUrl?: string | null;
}

export function useCustomizeBot(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CustomizeBotPayload) =>
      apiClient
        .patch(`/api/applications/${applicationId}/customize`, body)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.detail(applicationId),
      });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

// Custom bot deployment hooks

export const applicationDeploymentQueryOptions = (applicationId: string) =>
  queryOptions({
    queryKey: applicationsKeys.deployment(applicationId),
    queryFn: () =>
      apiClient
        .get<ApplicationDeploymentResponse>(
          `/api/applications/${applicationId}/deployment`
        )
        .then((r) => r.data.data),
    enabled: !!applicationId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "pending" || status === "deploying") return 5000;
      if (status === "running") return 30000;
      return false;
    },
  });

export function useApplicationDeployment(applicationId: string) {
  return useQuery(applicationDeploymentQueryOptions(applicationId));
}

export const deploymentLogsQueryOptions = (applicationId: string) =>
  queryOptions({
    queryKey: applicationsKeys.deploymentLogs(applicationId),
    queryFn: () =>
      apiClient
        .get<DeploymentLogsResponse>(
          `/api/applications/${applicationId}/deployment/logs`
        )
        .then((r) => r.data.data),
    enabled: !!applicationId,
  });

export function useDeploymentLogs(
  applicationId: string,
  deploymentStatus?: string
) {
  return useQuery({
    ...deploymentLogsQueryOptions(applicationId),
    refetchInterval:
      deploymentStatus === "pending" || deploymentStatus === "deploying"
        ? 5000
        : false,
  });
}

export function useCreateCustomBot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      name: string;
      tenantId: string;
      discordBotToken: string;
    }) => apiClient.post(`/api/applications`, body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useDeleteBot(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient
        .delete(`/api/applications/${applicationId}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

function useDeploymentMutation(applicationId: string, action: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient
        .post(`/api/applications/${applicationId}/deployment/${action}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.deployment(applicationId),
      });
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.deploymentLogs(applicationId),
      });
    },
  });
}

export function useStartBot(applicationId: string) {
  return useDeploymentMutation(applicationId, "start");
}

export function useStopBot(applicationId: string) {
  return useDeploymentMutation(applicationId, "stop");
}

export function useRestartBot(applicationId: string) {
  return useDeploymentMutation(applicationId, "restart");
}

export function useRedeployBot(applicationId: string) {
  return useDeploymentMutation(applicationId, "redeploy");
}

export function useRetryDeployment(applicationId: string) {
  return useDeploymentMutation(applicationId, "retry");
}

export function useUpdateBotCredentials(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { discordBotToken: string }) =>
      apiClient
        .patch(`/api/applications/${applicationId}/credentials`, body)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.deployment(applicationId),
      });
      queryClient.invalidateQueries({
        queryKey: applicationsKeys.detail(applicationId),
      });
    },
  });
}
